// --------------------------------------------------------------
// Created On: 2025-04-18
// Author: Hannah Vaughan
//
// Last Modified: 2026-05-13
// Modified By: Hannah Vaughan
//
// Copyright 2025 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React from "react";
import styles from "./DeviceTable.module.scss";
import useReduxDispatch from "src/hooks/useReduxDispatch";
import { useNavigate } from "react-router-dom";
import FilteredTableContainer from "src/components/FilteredTableContainer/FilteredTableContainer";
import SortedTableHeader from "src/components/SortedTableHeader/SortedTableHeader";
import useReduxSelector from "src/hooks/useReduxSelector";
import formatDashTimeString from "src/utilities/time/formatDashTimeString";
import formatDateLocal from "src/utilities/time/formatDateLocal";
import { stepIntoCompany } from "src/redux/features/currentUserSlice";
import userHasPermission from "src/utilities/userHasPermission";
import { VIEW_DEVICE_CONFIGURATION } from "src/constants/permissions";
import DeviceConfigurationButton from "./DeviceConfigurationButton/DeviceConfigurationButton";

// Table to display all device search results in the Super Admin Device Search Page.
export default function DeviceTable(props: Props): Component {
  const currentUser = useReduxSelector((state) => state.currentUser);
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();
  const userCanViewDeviceConfiguration = userHasPermission([[VIEW_DEVICE_CONFIGURATION]]);

  // Gets the device type's name from the provided model.
  function formatDeviceType(model: string): string {
    return props.deviceTypes.find((deviceType) => deviceType.model === model)?.name || "";
  }

  // Formats the provided date in mm/dd/yyyy local time format.
  function formatDate(date: string | null): string {
    if (date !== null && date.trim() !== "") {
      let dateString = formatDateLocal(date);
      const newDate = new Date(dateString);
      dateString = newDate.toISOString().split("T")[0];
      return formatDashTimeString(dateString);
    } else {
      return "";
    }
  }

  // Enters the company that the asset or device belongs to, then navigates to the asset if it exists,
  // and the company's monitoring device subscriptions page.
  async function navigateToAssetOrDevice(
    companyId: number,
    companyName: string,
    isPackager: boolean,
    assetId: number
  ): Promise<void> {
    dispatch(
      stepIntoCompany({
        companyId: companyId,
        companyName: companyName,
        isPackager: isPackager,
        permissions: currentUser.permissions,
      })
    );

    if (assetId !== null && assetId !== undefined) {
      navigate(`/asset/${assetId}/data`);
    } else {
      navigate("/monitoring-devices/subscriptions");
    }
  }

  return (
    <div className={`${styles.table} mb-0 pb-0`}>
      <FilteredTableContainer
        title={`Devices (${props.devices.length})`}
        filterPrompt="Filter devices..."
        filter=""
        hasContent={props.devices.length > 0}
        hasFilteredContent={false}
        showFilterInput={false}
        pluralContentType="devices"
        onChangeFilter={() => {
          /* Do nothing. */
        }}
      >
        <table className="table" data-test="device-search-table">
          <thead className={styles.header}>
            <tr>
              <SortedTableHeader
                name="Device Identifier"
                index={1}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
              />
              <SortedTableHeader
                name="Device Type"
                index={2}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
              />
              <SortedTableHeader
                name="Company"
                index={3}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
              />
              <SortedTableHeader
                name="Asset"
                index={4}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
              />
              <SortedTableHeader
                name="Activation Status"
                index={5}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
              />
              <SortedTableHeader
                name="Start Date"
                index={6}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
              />
              <SortedTableHeader
                name="End Date"
                index={7}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
              />
              <th className={styles.configurationHeader}>Configuration</th>
            </tr>
          </thead>
          <tbody>
            {props.devices.map((device) => (
              <tr
                key={`${device.deviceType}-${device.deviceIdentifier}-${device.companyId}`}
                className={`selectable-container ${styles.row}`}
              >
                <td
                  onClick={() =>
                    navigateToAssetOrDevice(device.companyId, device.companyName, device.isPackager, device.assetId)
                  }
                >
                  {device.deviceIdentifier}
                </td>
                <td
                  onClick={() =>
                    navigateToAssetOrDevice(device.companyId, device.companyName, device.isPackager, device.assetId)
                  }
                >
                  {formatDeviceType(device.deviceType)}
                </td>
                <td
                  onClick={() =>
                    navigateToAssetOrDevice(device.companyId, device.companyName, device.isPackager, device.assetId)
                  }
                >
                  {device.companyName}
                </td>
                <td
                  onClick={() =>
                    navigateToAssetOrDevice(device.companyId, device.companyName, device.isPackager, device.assetId)
                  }
                >
                  {device.assetId !== null ? device.assetName : "N/A"}
                </td>
                <td
                  onClick={() =>
                    navigateToAssetOrDevice(device.companyId, device.companyName, device.isPackager, device.assetId)
                  }
                >
                  {device.activationStatus}
                </td>
                <td
                  onClick={() =>
                    navigateToAssetOrDevice(device.companyId, device.companyName, device.isPackager, device.assetId)
                  }
                >
                  {formatDate(device.subscriptionStartDate)}
                </td>
                <td
                  onClick={() =>
                    navigateToAssetOrDevice(device.companyId, device.companyName, device.isPackager, device.assetId)
                  }
                >
                  {formatDate(device.subscriptionExpiry)}
                </td>
                <td>
                  {userCanViewDeviceConfiguration && (
                    <DeviceConfigurationButton
                      deviceId={device.deviceId !== null ? device.deviceId : null}
                      disabled={device.deviceType !== "COPILOTEDGE" || !userCanViewDeviceConfiguration}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </FilteredTableContainer>
    </div>
  );
}

interface Props {
  devices: Device[];
  deviceTypes: DeviceType[];
  sortColumnIndex: number;
  sortAscending: boolean;
  onUpdateSort: (newIndex: number) => void;
}

interface DeviceType {
  model: string;
  name: string;
}

interface Device {
  deviceId: number | null;
  deviceType: string;
  deviceIdentifier: number;
  assetId: number;
  assetName: string;
  companyId: number;
  companyName: string;
  isPackager: boolean;
  subscriptionExpiry: string;
  subscriptionStartDate: string;
  activationStatus: string;
}
